import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
  useVibesites,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Types
// ============================================================================

interface FaqItem {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

const DEFAULT_CATEGORY = 'General';

function normalizeCategory(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_CATEGORY;
  const trimmed = String(raw).trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_CATEGORY;
}

// ============================================================================
// Component
// ============================================================================

/**
 * FAQ page body: renders the `faqs` collection as an accordion, grouped by
 * category with a category filter above the list. Falls back to a friendly
 * empty state when no FAQs are configured on the property.
 */
export function FaqList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const { property } = useVibesites();

  const faqs = useCollectionBinding<FaqItem>('faqlist.items', {
    collection: 'faqs',
    defaultLimit: 50,
  });

  // Category buckets (preserves collection order for stable UX)
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const faq of faqs || []) {
      const cat = normalizeCategory(faq.category);
      if (!seen.includes(cat)) seen.push(cat);
    }
    return seen;
  }, [faqs]);

  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filtered = useMemo(() => {
    if (!faqs) return [] as FaqItem[];
    if (activeCategory === 'All') return faqs;
    return faqs.filter((faq) => normalizeCategory(faq.category) === activeCategory);
  }, [faqs, activeCategory]);

  const hasFaqs = filtered.length > 0;
  const showCategoryChips = categories.length > 1;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] py-12 md:py-20"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {showCategoryChips && (
          <div className="faq-chips" role="tablist" aria-label="FAQ categories">
            <button
              type="button"
              role="tab"
              aria-selected={activeCategory === 'All'}
              className={`faq-chip ${activeCategory === 'All' ? 'is-active' : ''}`}
              onClick={() => {
                setActiveCategory('All');
                setOpenIndex(0);
              }}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={activeCategory === cat}
                className={`faq-chip ${activeCategory === cat ? 'is-active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenIndex(0);
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {hasFaqs ? (
            <ul className="faq-list" role="list">
              {filtered.map((faq, i) => {
                const isOpen = openIndex === i;
                const questionText = faq.question?.trim() || 'Question';
                const answerHtml = faq.answer
                  ? resolveRichText(faq.answer, property as Record<string, unknown>)
                  : '';
                const panelId = `faq-panel-${i}`;
                const buttonId = `faq-button-${i}`;
                return (
                  <li key={i} className={`faq-item ${isOpen ? 'is-open' : ''}`}>
                    <h3 className="faq-question-wrap">
                      <button
                        id={buttonId}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        className="faq-question"
                        onClick={() => setOpenIndex(isOpen ? null : i)}
                      >
                        <span className="faq-question-text">{questionText}</span>
                        <span aria-hidden="true" className="faq-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      hidden={!isOpen}
                      className="faq-answer"
                    >
                      {answerHtml ? (
                        <div
                          className="faq-answer-body"
                          dangerouslySetInnerHTML={{ __html: answerHtml }}
                        />
                      ) : (
                        <p className="faq-answer-empty">No answer provided yet.</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="faq-empty">
              <p className="font-body text-base text-[var(--vibe-color-text-muted)]">
                No FAQs have been added yet. Check back soon.
              </p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
          margin-bottom: 2rem;
        }
        .faq-chip {
          font-family: var(--vibe-font-heading);
          font-size: 0.75rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 0.5rem 1.1rem;
          border-radius: 9999px;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-primary) 45%, transparent);
          background: transparent;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s;
        }
        .faq-chip:hover {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .faq-chip.is-active {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border-color: var(--vibe-color-primary);
        }

        .faq-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-item {
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: 6px;
          overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-item.is-open {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 55%, transparent);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
          background: #ffffff;
        }

        .faq-question-wrap {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-weight: 500;
        }
        .faq-question {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          text-align: left;
          padding: 1rem 1.25rem;
          background: transparent;
          border: 0;
          cursor: pointer;
          font-family: inherit;
          font-size: 1rem;
          color: var(--vibe-color-text);
          line-height: 1.4;
        }
        .faq-question:hover .faq-question-text {
          color: var(--vibe-color-primary);
        }
        .faq-question-text {
          flex: 1 1 auto;
          transition: color 0.2s;
        }
        .faq-icon {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 9999px;
          color: var(--vibe-color-primary);
          transition: transform 0.25s ease, background 0.2s ease;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
        }
        .faq-item.is-open .faq-icon {
          transform: rotate(180deg);
          background: color-mix(in srgb, var(--vibe-color-primary) 18%, transparent);
        }

        .faq-answer {
          padding: 0 1.25rem 1.25rem;
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          font-size: 0.95rem;
          line-height: 1.6;
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-primary) 18%, transparent);
        }
        .faq-answer[hidden] {
          display: none;
        }
        .faq-answer-body {
          padding-top: 1rem;
        }
        .faq-answer-body p { margin-bottom: 0.75rem; }
        .faq-answer-body p:last-child { margin-bottom: 0; }
        .faq-answer-body p:empty { display: none; }
        .faq-answer-body ul,
        .faq-answer-body ol {
          padding-left: 1.25rem;
          margin-bottom: 0.75rem;
        }
        .faq-answer-body ul { list-style: disc; }
        .faq-answer-body ol { list-style: decimal; }
        .faq-answer-body li { margin-bottom: 0.25rem; }
        .faq-answer-body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer-empty {
          padding-top: 1rem;
          font-style: italic;
        }

        .faq-empty {
          border: 1px dashed #d5d5d8;
          border-radius: 6px;
          padding: 2rem 1.25rem;
          text-align: center;
          background: #f7f7f8;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
      defaultLimit: 50,
      description:
        'Questions and answers shown in the accordion. Grouped by category when more than one is present.',
    },
  },
};

(FaqList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqList;
